import { NextRequest, NextResponse } from "next/server";
import { getBoxStatus } from "@/lib/scheduler";

// Colors for focus set boxes only
const BOX_COLORS = {
  box0: "#dbdbdb",    // Light gray - New
  box1: "#ef4444",    // Red - Learning (needs work)
  box2: "#fff6c7",    // Pale yellow - Familiar
  box3: "#22c55e",    // Green - Stable (good!)
};

const MASTERED_COLOR = "#22c55e";  // Green
const UNMASTERED_COLOR = "#374151";  // Dark gray

function generateDonutPath(data: { value: number; color: string }[], cx: number, cy: number, radius: number): string {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) {
    return `<circle cx="${cx}" cy="${cy}" r="${radius}" fill="#374151" />`;
  }

  let paths = "";
  let startAngle = -Math.PI / 2; // Start from top

  for (const segment of data) {
    if (segment.value === 0) continue;

    const sliceAngle = (segment.value / total) * 2 * Math.PI;
    const endAngle = startAngle + sliceAngle;

    const x1 = cx + radius * Math.cos(startAngle);
    const y1 = cy + radius * Math.sin(startAngle);
    const x2 = cx + radius * Math.cos(endAngle);
    const y2 = cy + radius * Math.sin(endAngle);

    const largeArc = sliceAngle > Math.PI ? 1 : 0;

    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    paths += `<path d="${d}" fill="${segment.color}" />`;

    startAngle = endAngle;
  }

  // Center hole for donut effect
  paths += `<circle cx="${cx}" cy="${cy}" r="${radius * 0.55}" fill="#111827" />`;

  return paths;
}

function generateVerticalBar(mastered: number, total: number, x: number, y: number, width: number, height: number): string {
  const percent = total > 0 ? mastered / total : 0;
  const filledHeight = height * percent;
  const emptyHeight = height - filledHeight;
  const cornerRadius = width / 2;

  return `
    <!-- Background bar -->
    <rect x="${x}" y="${y}" width="${width}" height="${height}" rx="${cornerRadius}" fill="${UNMASTERED_COLOR}" />
    <!-- Filled portion (from bottom) -->
    ${filledHeight > 0 ? `<rect x="${x}" y="${y + emptyHeight}" width="${width}" height="${filledHeight}" rx="${cornerRadius}" fill="${MASTERED_COLOR}" />` : ''}
  `;
}

export async function GET(request: NextRequest) {
  try {
    const { searchParams } = new URL(request.url);
    const userId = searchParams.get("userId") || "default-user";
    const size = parseInt(searchParams.get("size") || "120");

    const boxStatus = await getBoxStatus(userId);
    const totalProblems = boxStatus.totalProblems;

    // Build pie chart data - focus set only (Box 0-3)
    const chartData: { value: number; color: string }[] = [];
    let mastered = 0;

    if (boxStatus.phase === 1 && boxStatus.phase1) {
      const p1 = boxStatus.phase1;
      chartData.push(
        { value: p1.box0, color: BOX_COLORS.box0 },
        { value: p1.box1, color: BOX_COLORS.box1 },
        { value: p1.box2, color: BOX_COLORS.box2 },
        { value: p1.box3, color: BOX_COLORS.box3 },
      );
      // Mastered = Box 3 (stable) + Graduated
      mastered = p1.box3 + p1.graduated;
    } else if (boxStatus.phase === 2 && boxStatus.phase2) {
      // Phase 2: map boxes 1-5 to similar colors
      const p2 = boxStatus.phase2;
      chartData.push(
        { value: p2.box1, color: BOX_COLORS.box1 },
        { value: p2.box2, color: BOX_COLORS.box2 },
        { value: p2.box3, color: BOX_COLORS.box3 },
        { value: p2.box4, color: "#4ade80" },  // Lighter green
        { value: p2.box5, color: "#22c55e" },  // Green
      );
      // Phase 2: Box 3+ is mastered
      mastered = p2.box3 + p2.box4 + p2.box5;
    }

    // Layout: donut on left, vertical bar on right
    const padding = 4;
    const gap = 12;
    const barWidth = 16;
    const donutSize = size;
    const totalWidth = donutSize + gap + barWidth + padding;
    const totalHeight = size;

    const donutCx = donutSize / 2;
    const donutCy = donutSize / 2;
    const donutRadius = donutSize / 2 - padding;

    const barX = donutSize + gap;
    const barY = padding;
    const barHeight = size - padding * 2;

    const donutPaths = generateDonutPath(chartData, donutCx, donutCy, donutRadius);
    const barSvg = generateVerticalBar(mastered, totalProblems, barX, barY, barWidth, barHeight);

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${totalWidth}" height="${totalHeight}" viewBox="0 0 ${totalWidth} ${totalHeight}">
        ${donutPaths}
        ${barSvg}
      </svg>
    `;

    return new NextResponse(svg, {
      headers: {
        "Content-Type": "image/svg+xml",
        "Cache-Control": "no-cache, no-store, must-revalidate",
      },
    });
  } catch (error) {
    console.error("Progress chart error:", error);
    return NextResponse.json(
      { error: "Failed to generate chart" },
      { status: 500 }
    );
  }
}
